import { FC, useContext } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { InfoOutlined, CheckOutlined } from '@mui/icons-material';
import { Trans, useTranslation } from 'next-i18next';

import { EXTERNAL_ROUTES } from '@/libs/routing';
import type { IProductMetadata } from '@/features/pricingV2/domain';
import { PricingPageContext } from '@/features/pricingV2/ui/contexts';
import { BillingCycle } from '@/features/common/pricing';

import { PlanCardAction } from './components/PlanCardAction';

import InfinityIcon from './assets/infinity.svg';
import StorageIcon from './assets/Storage.svg';

import styles from './styles.module.scss';

const PlanCardFeatures: FC<{
  features: string[];
  metadata: IProductMetadata['variants'][BillingCycle.Monthly | BillingCycle.Yearly];
}> = ({ features, metadata }) => {
  const { t: tFeature } = useTranslation('pricingV2', {
    keyPrefix: 'planFeature',
  });

  return (
    <Box className={styles.features}>
      <ul className={styles.featuresList}>
        {features.map((feature) => {
          if (feature === 'bulk_credits') {
            return (
              <li key={feature} className={styles.feature}>
                <Box className={styles.featureContent}>
                  <CheckOutlined />
                  <Typography className={styles.featureTitle}>
                    {metadata.bulkCredits === 'unlimited'
                      ? tFeature(`${feature}.label_unlimited`)
                      : tFeature(`${feature}.label`, {
                          count: metadata.bulkCredits,
                        })}
                  </Typography>
                </Box>
                <Box className={styles.featureTooltip}>
                  <Tooltip title={tFeature(`${feature}.tooltip`)}>
                    <InfoOutlined />
                  </Tooltip>
                </Box>
              </li>
            );
          }

          return (
            <li key={feature} className={styles.feature}>
              <Box className={styles.featureContent}>
                <CheckOutlined />
                <Typography className={styles.featureTitle}>
                  {tFeature(`${feature}.label`)}
                </Typography>
              </Box>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};

const PlanCardCredits: FC<{
  isCustom: boolean;
  tKey: string;
  billingCycle: BillingCycle.Monthly | BillingCycle.Yearly;
  metadata: IProductMetadata['variants'][BillingCycle.Monthly | BillingCycle.Yearly];
}> = ({ tKey, isCustom, billingCycle, metadata }) => {
  const { t: tOverview } = useTranslation('pricingV2', {
    keyPrefix: tKey,
  });

  if (isCustom) {
    return (
      <Box className={clsx(styles.credits, styles.primary)}>
        <Box className={styles.creditsValue}>
          {billingCycle === BillingCycle.Monthly
            ? tOverview('credits.title_monthly')
            : tOverview('credits.title_yearly')}
        </Box>
        <Box className={styles.creditsTooltip}>
          <Tooltip
            title={
              <Trans
                t={tOverview}
                i18nKey="credits.tooltip"
                components={{
                  a: (
                    <a
                      style={{ color: 'white' }}
                      href={EXTERNAL_ROUTES.HELP_CENTER_FAIR_USAGE_POLICY}
                    />
                  ),
                }}
              />
            }
          >
            <InfoOutlined />
          </Tooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.credits}>
      <Box className={styles.creditsIcon}>
        <StorageIcon />
      </Box>
      <Box className={styles.creditsValue}>
        {billingCycle === BillingCycle.Monthly
          ? tOverview('credits.title_monthly', {
              count: metadata.fullCredits === 'unlimited' ? 0 : metadata.fullCredits,
            })
          : tOverview('credits.title_yearly', {
              count: metadata.fullCredits === 'unlimited' ? 0 : metadata.fullCredits,
            })}
      </Box>
      <Box className={styles.creditsTooltip}>
        <Tooltip
          title={
            <Trans
              t={tOverview}
              i18nKey="credits.tooltip"
              components={{
                a: (
                  <a
                    style={{ color: 'white' }}
                    href={EXTERNAL_ROUTES.HELP_CENTER_FAIR_USAGE_POLICY}
                  />
                ),
              }}
            />
          }
        >
          <InfoOutlined />
        </Tooltip>
      </Box>
    </Box>
  );
};

export const PlanCard: FC<{
  metadata: IProductMetadata;
}> = ({ metadata }) => {
  const { billingCycle } = useContext(PricingPageContext);
  const metadataByCycle = metadata.variants[billingCycle];

  const getKey = (): string => {
    const isFullCreditsUnlimited = metadataByCycle.fullCredits === 'unlimited';

    switch (true) {
      case metadata.isCustom:
        return `planOverview.custom`;
      case isFullCreditsUnlimited:
        return `planOverview.unlimited`;
      default:
        return `planOverview.creditBased`;
    }
  };
  const tKey = getKey();
  const { t: tOverview } = useTranslation('pricingV2', {
    keyPrefix: tKey,
  });

  return (
    <Box
      className={clsx(styles.card, {
        [styles.active]: metadata.mostPopular,
      })}
    >
      {metadata.mostPopular && (
        <Box className={styles.cardBadge}>
          <Typography className={styles.cardBadgeTitle}>{tOverview('badge')}</Typography>
        </Box>
      )}
      <Box className={styles.header}>
        <Typography className={styles.headerTitle}>
          {tOverview('title', { name: metadata.name })}
        </Typography>
        {metadata.isCustom && (
          <Box className={styles.headerIcon}>
            <InfinityIcon />
          </Box>
        )}
      </Box>
      <Box className={styles.price}>
        <span className={styles.priceValue}>
          {tOverview('price.value', {
            price:
              metadataByCycle.price === 'contact-sales'
                ? 'Contact Sales'
                : metadataByCycle.price.price,
          })}
        </span>
        {!metadata.isCustom && (
          <Stack direction="column">
            <span className={styles.priceText}>{tOverview('price.per_user')}</span>
            <span className={styles.priceText}>
              {billingCycle === BillingCycle.Monthly
                ? tOverview('price.period_monthly')
                : tOverview('price.period_yearly')}
            </span>
          </Stack>
        )}
      </Box>
      <PlanCardCredits
        isCustom={metadata.isCustom}
        tKey={tKey}
        billingCycle={billingCycle}
        metadata={metadataByCycle}
      />
      <PlanCardAction metadata={metadata} tKey={tKey} />
      <PlanCardFeatures features={metadataByCycle.features} metadata={metadataByCycle} />
    </Box>
  );
};
